import { useState, useEffect, useCallback } from 'react'
import { Bell, Plus, Trash2, Edit2, Search, Loader, AlertTriangle, CheckCircle, XCircle, ChevronDown, Users, RefreshCw } from 'lucide-react'
import { alarmApi } from '../lib/alarmApi'
import { projectsApi } from '../lib/projectsApi'
import { useToast } from '../context/ToastContext'
import { SecureInput, SecureTextarea } from '../components/SecureInput'
import { scanInput } from '../lib/inputSecurity'

const ALARM_TYPES = ['threshold', 'schedule', 'last_contact', 'custom']

const CONDITION_SYMBOLS = ['<', '<=', '=', '>=', '>']

function StatCard({ label, value, icon: Icon, colorClass }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5 flex items-center gap-4">
      <div className={`p-3 rounded-lg ${colorClass}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div>
        <p className="text-2xl font-bold text-[#4D4D4D]">{value}</p>
        <p className="text-sm text-gray-500">{label}</p>
      </div>
    </div>
  )
}

function AlarmStatusBadge({ triggered, enabled }) {
  if (!enabled) return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-500">Disabled</span>
  if (triggered) return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-600"><AlertTriangle className="w-3 h-3" />Triggered</span>
  return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-600"><CheckCircle className="w-3 h-3" />Active</span>
}

export function AlarmsPage() {
  const toast = useToast()

  const [projects, setProjects] = useState([])
  const [currentProject, setCurrentProject] = useState(null)
  const [loadingProjects, setLoadingProjects] = useState(true)

  const [alarms, setAlarms] = useState([])
  const [loadingAlarms, setLoadingAlarms] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  const [recipientGroups, setRecipientGroups] = useState([])
  const [loadingGroups, setLoadingGroups] = useState(false)
  const [activeSection, setActiveSection] = useState('alarms') // 'alarms' | 'groups'

  // Create/Edit Alarm modal
  const [showAlarmModal, setShowAlarmModal] = useState(false)
  const [editingAlarm, setEditingAlarm] = useState(null)
  const [alarmForm, setAlarmForm] = useState({ name: '', description: '', type: 'threshold', enabled: true })
  const [savingAlarm, setSavingAlarm] = useState(false)

  // Create/Edit Group modal
  const [showGroupModal, setShowGroupModal] = useState(false)
  const [editingGroup, setEditingGroup] = useState(null)
  const [groupForm, setGroupForm] = useState({ name: '', recipients: '' })
  const [savingGroup, setSavingGroup] = useState(false)

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState(null) // { type: 'alarm'|'group', id, name }

  // Project dropdown
  const [showProjectDropdown, setShowProjectDropdown] = useState(false)

  useEffect(() => {
    const load = async () => {
      setLoadingProjects(true)
      try {
        const res = await projectsApi.fetchProjects(1, 100)
        if (res?.success && Array.isArray(res.data)) {
          setProjects(res.data)
          if (res.data.length > 0) setCurrentProject(res.data[0])
        }
      } catch {
        toast.error('Failed to load projects')
      } finally {
        setLoadingProjects(false)
      }
    }
    load()
  }, [])

  const fetchAlarms = useCallback(async () => {
    if (!currentProject) return
    setLoadingAlarms(true)
    try {
      const res = await alarmApi.getAlarms({ project: currentProject.id })
      setAlarms(Array.isArray(res.data) ? res.data : res.data?.results || [])
    } catch {
      toast.error('Failed to load alarms')
    } finally {
      setLoadingAlarms(false)
    }
  }, [currentProject])

  const fetchGroups = useCallback(async () => {
    if (!currentProject) return
    setLoadingGroups(true)
    try {
      const res = await alarmApi.getRecipientGroups(currentProject.id)
      setRecipientGroups(Array.isArray(res.data) ? res.data : res.data?.results || [])
    } catch {
      toast.error('Failed to load recipient groups')
    } finally {
      setLoadingGroups(false)
    }
  }, [currentProject])

  useEffect(() => {
    if (currentProject) {
      fetchAlarms()
      fetchGroups()
    }
  }, [currentProject])

  const filteredAlarms = alarms.filter(a =>
    a.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    a.description?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const stats = {
    total: alarms.length,
    triggered: alarms.filter(a => a.triggered).length,
    active: alarms.filter(a => a.enabled && !a.triggered).length,
    disabled: alarms.filter(a => !a.enabled).length,
  }

  // Alarm modal handlers
  const openCreateAlarm = () => {
    setEditingAlarm(null)
    setAlarmForm({ name: '', description: '', type: 'threshold', enabled: true })
    setShowAlarmModal(true)
  }

  const openEditAlarm = (alarm) => {
    setEditingAlarm(alarm)
    setAlarmForm({
      name: alarm.name || '',
      description: alarm.description || '',
      type: alarm.type || 'threshold',
      enabled: !!alarm.enabled,
    })
    setShowAlarmModal(true)
  }

  const handleSaveAlarm = async () => {
    if (!alarmForm.name.trim()) { toast.warning('Alarm name is required'); return }
    const secErr = scanInput(alarmForm.name) || scanInput(alarmForm.description)
    if (secErr) { toast.error(secErr); return }
    setSavingAlarm(true)
    try {
      const payload = {
        ...alarmForm,
        enabled: alarmForm.enabled ? 1 : 0,
        project: currentProject.id,
      }
      if (editingAlarm) {
        await alarmApi.partialUpdateAlarm(editingAlarm.id, payload)
        toast.success('Alarm updated')
      } else {
        await alarmApi.createAlarm(payload)
        toast.success('Alarm created')
      }
      setShowAlarmModal(false)
      fetchAlarms()
    } catch {
      toast.error('Failed to save alarm')
    } finally {
      setSavingAlarm(false)
    }
  }

  // Recipient group handlers
  const openCreateGroup = () => {
    setEditingGroup(null)
    setGroupForm({ name: '', recipients: '' })
    setShowGroupModal(true)
  }

  const openEditGroup = (group) => {
    setEditingGroup(group)
    setGroupForm({ name: group.name || '', recipients: group.recipients || '' })
    setShowGroupModal(true)
  }

  const handleSaveGroup = async () => {
    if (!groupForm.name.trim()) { toast.warning('Group name is required'); return }
    const secErr = scanInput(groupForm.name) || scanInput(groupForm.recipients)
    if (secErr) { toast.error(secErr); return }
    setSavingGroup(true)
    try {
      const payload = { ...groupForm, project: currentProject.id }
      if (editingGroup) {
        await alarmApi.updateRecipientGroup(editingGroup.id, payload)
        toast.success('Group updated')
      } else {
        await alarmApi.createRecipientGroup(payload)
        toast.success('Group created')
      }
      setShowGroupModal(false)
      fetchGroups()
    } catch {
      toast.error('Failed to save group')
    } finally {
      setSavingGroup(false)
    }
  }

  // Delete
  const handleDelete = async () => {
    if (!deleteTarget) return
    try {
      if (deleteTarget.type === 'alarm') {
        await alarmApi.deleteAlarm(deleteTarget.id)
        toast.success('Alarm deleted')
        fetchAlarms()
      } else {
        await alarmApi.deleteRecipientGroup(deleteTarget.id)
        toast.success('Group deleted')
        fetchGroups()
      }
    } catch {
      toast.error('Failed to delete')
    } finally {
      setDeleteTarget(null)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-[1920px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Bell className="w-5 h-5 text-[#358ECD]" />
            <h1 className="text-xl font-semibold text-[#4D4D4D]">Alarms</h1>
          </div>

          {/* Project Selector */}
          <div className="relative">
            <button
              onClick={() => setShowProjectDropdown(v => !v)}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm text-[#4D4D4D] hover:border-[#358ECD] transition"
            >
              {loadingProjects ? (
                <Loader className="w-4 h-4 animate-spin text-gray-400" />
              ) : (
                <>
                  <span className="">{currentProject?.name || 'Select Project'}</span>
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                </>
              )}
            </button>
            {showProjectDropdown && (
              <div className="absolute right-0 top-full mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
                {projects.map(p => (
                  <button
                    key={p.id}
                    onClick={() => { setCurrentProject(p); setShowProjectDropdown(false) }}
                    className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition ${currentProject?.id === p.id ? 'text-[#358ECD]' : 'text-[#4D4D4D]'}`}
                  >
                    {p.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-[1920px] mx-auto px-6 py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard label="Total Alarms" value={stats.total} icon={Bell} colorClass="bg-[#358ECD]" />
          <StatCard label="Triggered" value={stats.triggered} icon={AlertTriangle} colorClass="bg-red-500" />
          <StatCard label="Active" value={stats.active} icon={CheckCircle} colorClass="bg-green-500" />
          <StatCard label="Disabled" value={stats.disabled} icon={XCircle} colorClass="bg-gray-400" />
        </div>

        {/* Section Tabs */}
        <div className="flex items-center gap-1 mb-4 border-b border-gray-200">
          {[
            { key: 'alarms', label: 'Alarms', icon: Bell },
            { key: 'groups', label: 'Recipient Groups', icon: Users },
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveSection(key)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm border-b-2 transition -mb-px ${
                activeSection === key
                  ? 'border-[#358ECD] text-[#358ECD]'
                  : 'border-transparent text-gray-500 hover:text-[#4D4D4D]'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        {/* Alarms Section */}
        {activeSection === 'alarms' && (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            {/* Toolbar */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <div className="relative flex-1 max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search alarms..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="pl-9 pr-4 py-1.5 w-full border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#358ECD]"
                />
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={fetchAlarms}
                  className="p-1.5 text-gray-400 hover:text-[#358ECD] rounded transition"
                  title="Refresh"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
                <button
                  onClick={openCreateAlarm}
                  className="flex items-center gap-2 px-4 py-1.5 bg-[#358ECD] text-white text-sm rounded-lg hover:bg-[#2d7ab8] transition"
                >
                  <Plus className="w-4 h-4" />
                  New Alarm
                </button>
              </div>
            </div>

            {/* Table */}
            {loadingAlarms ? (
              <div className="flex items-center justify-center py-16 text-gray-400">
                <Loader className="w-6 h-6 animate-spin mr-2" />
                <span className="text-sm">Loading alarms...</span>
              </div>
            ) : filteredAlarms.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                <Bell className="w-10 h-10 mb-3 opacity-30" />
                <p className="text-sm">No alarms found</p>
                <p className="text-xs mt-1">{searchQuery ? 'Try a different search term' : 'Create your first alarm to get started'}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                      <th className="px-4 py-2.5 text-left">Name</th>
                      <th className="px-4 py-2.5 text-left">Type</th>
                      <th className="px-4 py-2.5 text-left">Status</th>
                      <th className="px-4 py-2.5 text-left">Count</th>
                      <th className="px-4 py-2.5 text-left">Last Checked</th>
                      <th className="px-4 py-2.5 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredAlarms.map(alarm => (
                      <tr key={alarm.id} className="hover:bg-gray-50 transition">
                        <td className="px-4 py-3">
                          <p className="text-[#4D4D4D]">{alarm.name}</p>
                          {alarm.description && <p className="text-xs text-gray-400 mt-0.5 truncate max-w-[220px]">{alarm.description}</p>}
                        </td>
                        <td className="px-4 py-3 text-gray-500 capitalize">{alarm.type?.replace('_', ' ')}</td>
                        <td className="px-4 py-3">
                          <AlarmStatusBadge triggered={alarm.triggered} enabled={alarm.enabled} />
                        </td>
                        <td className="px-4 py-3 text-gray-500">{alarm.count ?? '—'}</td>
                        <td className="px-4 py-3 text-gray-400 text-xs">
                          {alarm.last_checked ? new Date(alarm.last_checked).toLocaleString() : '—'}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => openEditAlarm(alarm)}
                              className="p-1.5 text-gray-400 hover:text-[#358ECD] rounded transition"
                              title="Edit"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setDeleteTarget({ type: 'alarm', id: alarm.id, name: alarm.name })}
                              className="p-1.5 text-gray-400 hover:text-red-500 rounded transition"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Recipient Groups Section */}
        {activeSection === 'groups' && (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <h2 className="text-sm text-[#4D4D4D]">Recipient Groups</h2>
              <button
                onClick={openCreateGroup}
                className="flex items-center gap-2 px-4 py-1.5 bg-[#358ECD] text-white text-sm rounded-lg hover:bg-[#2d7ab8] transition"
              >
                <Plus className="w-4 h-4" />
                New Group
              </button>
            </div>

            {loadingGroups ? (
              <div className="flex items-center justify-center py-16 text-gray-400">
                <Loader className="w-6 h-6 animate-spin mr-2" />
                <span className="text-sm">Loading groups...</span>
              </div>
            ) : recipientGroups.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                <Users className="w-10 h-10 mb-3 opacity-30" />
                <p className="text-sm">No recipient groups</p>
                <p className="text-xs mt-1">Create a group to organize alarm notification recipients</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {recipientGroups.map(group => (
                  <div key={group.id} className="flex items-start justify-between px-4 py-3 hover:bg-gray-50 transition">
                    <div className="flex-1 min-w-0">
                      <p className="text-[#4D4D4D] text-sm">{group.name}</p>
                      <p className="text-xs text-gray-400 mt-0.5 truncate">{group.recipients || 'No recipients set'}</p>
                    </div>
                    <div className="flex items-center gap-1 ml-4">
                      <button
                        onClick={() => openEditGroup(group)}
                        className="p-1.5 text-gray-400 hover:text-[#358ECD] rounded transition"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setDeleteTarget({ type: 'group', id: group.id, name: group.name })}
                        className="p-1.5 text-gray-400 hover:text-red-500 rounded transition"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Alarm Create/Edit Modal */}
      {showAlarmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-base font-semibold text-[#4D4D4D]">{editingAlarm ? 'Edit Alarm' : 'Create Alarm'}</h2>
              <button onClick={() => setShowAlarmModal(false)} className="text-gray-400 hover:text-gray-600 transition">
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Alarm Name <span className="text-red-400">*</span></label>
                <SecureInput
                  type="text"
                  value={alarmForm.name}
                  onChange={e => setAlarmForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Enter alarm name"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#358ECD]"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Description</label>
                <SecureInput
                  type="text"
                  value={alarmForm.description}
                  onChange={e => setAlarmForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Optional description"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#358ECD]"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Type</label>
                <select
                  value={alarmForm.type}
                  onChange={e => setAlarmForm(f => ({ ...f, type: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#358ECD]"
                >
                  {ALARM_TYPES.map(t => (
                    <option key={t} value={t}>{t.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
                  ))}
                </select>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={alarmForm.enabled}
                  onChange={e => setAlarmForm(f => ({ ...f, enabled: e.target.checked }))}
                  className="w-4 h-4 accent-[#358ECD]"
                />
                <span className="text-sm text-[#4D4D4D]">Enable alarm</span>
              </label>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100">
              <button
                onClick={() => setShowAlarmModal(false)}
                className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveAlarm}
                disabled={savingAlarm}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-[#358ECD] text-white rounded-lg hover:bg-[#2d7ab8] transition disabled:opacity-60"
              >
                {savingAlarm && <Loader className="w-3.5 h-3.5 animate-spin" />}
                {editingAlarm ? 'Save Changes' : 'Create Alarm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Group Create/Edit Modal */}
      {showGroupModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-base font-semibold text-[#4D4D4D]">{editingGroup ? 'Edit Group' : 'Create Recipient Group'}</h2>
              <button onClick={() => setShowGroupModal(false)} className="text-gray-400 hover:text-gray-600 transition">
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Group Name <span className="text-red-400">*</span></label>
                <SecureInput
                  type="text"
                  value={groupForm.name}
                  onChange={e => setGroupForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Enter group name"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#358ECD]"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Recipients</label>
                <SecureTextarea
                  value={groupForm.recipients}
                  onChange={e => setGroupForm(f => ({ ...f, recipients: e.target.value }))}
                  placeholder="Email addresses or phone numbers, one per line"
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#358ECD] resize-none"
                />
                <p className="text-xs text-gray-400 mt-1">Enter email addresses or phone numbers separated by commas or newlines</p>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100">
              <button
                onClick={() => setShowGroupModal(false)}
                className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveGroup}
                disabled={savingGroup}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-[#358ECD] text-white rounded-lg hover:bg-[#2d7ab8] transition disabled:opacity-60"
              >
                {savingGroup && <Loader className="w-3.5 h-3.5 animate-spin" />}
                {editingGroup ? 'Save Changes' : 'Create Group'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm mx-4">
            <div className="px-6 py-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                </div>
                <h2 className="text-base font-semibold text-[#4D4D4D]">Confirm Delete</h2>
              </div>
              <p className="text-sm text-gray-600">
                Are you sure you want to delete <span className="text-[#4D4D4D]">"{deleteTarget.name}"</span>? This action cannot be undone.
              </p>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Backdrop for project dropdown */}
      {showProjectDropdown && (
        <div className="fixed inset-0 z-40" onClick={() => setShowProjectDropdown(false)} />
      )}
    </div>
  )
}
